import React, { useEffect, useState } from 'react';
import { Alert, Button, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import type { E2IConfig, RegisterCode } from '../../types/e2i';
import { REGISTER_CODES, REGISTER_NAMES } from '../../types/e2i';
import { errorText, registerLabel } from '../../utils/e2i';

interface Props {
  open: boolean;
  /** Shown in the title, e.g. the enquiry number. */
  subject: string;
  current: RegisterCode | null;
  config: E2IConfig | null;
  onClose: () => void;
  onSave: (code: RegisterCode, note: string) => Promise<void>;
}

/** Sets an enquiry's register status by hand (e2i.register); the note is kept in the history. */
const RegisterDialog: React.FC<Props> = ({ open, subject, current, config, onClose, onSave }) => {
  const [code, setCode] = useState<RegisterCode | ''>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCode(current || '');
    setNote('');
    setBusy(false);
    setError(null);
  }, [open, current]);

  const codes: Array<{ code: RegisterCode; name: string }> = config?.registerCodes?.length
    ? config.registerCodes
    : REGISTER_CODES.map((c) => ({ code: c, name: REGISTER_NAMES[c] }));

  const save = async () => {
    if (!code || !note.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(code, note.trim());
    } catch (e: any) {
      setError(errorText(e, 'The register status could not be set'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="e2i-register-title">
      <DialogTitle id="e2i-register-title">Register status{subject ? `: ${subject}` : ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            PRPO sets the register status as the enquiry moves on. Setting it by hand overrides that; the note is kept in the history.
          </Typography>
          <TextField id="e2i-register-code" select label="Register status" value={code} onChange={(e) => setCode(Number(e.target.value) as RegisterCode)} fullWidth required>
            {codes.map((c) => <MenuItem key={c.code} value={c.code}>{registerLabel(c.code, c.name)}</MenuItem>)}
          </TextField>
          <TextField
            id="e2i-register-note"
            label="Note (required)"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 1000))}
            multiline
            minRows={2}
            fullWidth
            required
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>Back</Button>
        <Button onClick={() => { void save(); }} disabled={busy || !code || !note.trim()}>{busy ? 'Saving…' : 'Set status'}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default RegisterDialog;
